import { useEffect, useMemo, useRef, useState } from 'react'

import http, { getAuthInfo } from './http'
import { TApi } from './const'

/**
 * Holt Profilbilder und Firmenlogos angemeldet und macht sie anzeigbar.
 *
 * Warum überhaupt: Die Bilder lagen bis 01.08.2026 hinter einer Adresse ohne
 * Anmeldung. Darunter sind Gesichter minderjähriger Schüler:innen — wer die
 * Adresse hatte, kam an das Bild, auch ohne Zugang zur Anwendung. Adressen
 * stehen im Quelltext jeder Seite, auf der ein Bild vorkommt.
 *
 * Ein `<img src="…">` schickt keinen Anmelde-Kopfeintrag mit. Deshalb holen wir
 * das Bild selbst und geben dem `<img>` eine lokale Adresse auf die geladenen
 * Daten. Dasselbe Muster nutzt bereits `useAuthImages` für Ticket-Screenshots;
 * hier ist es auf Dateinamen statt auf ganze Adressen zugeschnitten, weil die
 * Anwendung überall den Hashnamen in der Hand hat.
 *
 * Die lokalen Adressen müssen wieder freigegeben werden. Ohne das sammelt der
 * Browser beim Blättern durch Listen Bilder an, die er nie mehr loswird.
 *
 * @param {Array<string|null|undefined>} hashNames Dateinamen, Leeres wird übersprungen
 * @returns {Object} Dateiname → lokale Adresse; fehlt ein Eintrag, lädt er noch
 */
export function useAvatarUrls(hashNames) {
	const [urls, setUrls] = useState({})

	// Über den Inhalt vergleichen, nicht über die Liste selbst: Aufrufer bauen
	// sie bei jedem Rendern neu, die Namen darin sind aber dieselben. Ohne das
	// liefe der Effekt endlos.
	const key = useMemo(
		() =>
			[...new Set((hashNames || []).filter(Boolean))]
				.sort()
				.join('|'),
		[hashNames]
	)

	// Bereits geladene Bilder über einen Wechsel der Liste hinweg behalten —
	// beim Blättern kommen oft dieselben Personen wieder vor.
	const cache = useRef({})

	/*
	 * Ohne Anmeldung gibt es die Bilder nicht — der Abruf würde nur scheitern.
	 * Das passiert nicht nur auf der Anmeldeseite: Läuft die Sitzung ab, bleibt
	 * die Liste bis zum Abmelden stehen und schickt für jede Person eine
	 * Anfrage hinterher. In der Konsole standen daraufhin reihenweise rote
	 * Fehler zu `/api/private/files/avatar/…`. Als Abhängigkeit mitgeführt,
	 * damit die Bilder nach einer Anmeldung ohne Neuaufbau nachgeladen werden.
	 */
	const token = getAuthInfo().token

	useEffect(() => {
		if (!token) return

		const names = key ? key.split('|') : []
		const missing = names.filter(name => !cache.current[name])

		if (!missing.length) {
			setUrls({ ...cache.current })
			return
		}

		let cancelled = false

		Promise.all(
			missing.map(name =>
				http
					.get(TApi.PROFILE_AVATAR(name), { responseType: 'blob', disableToast: true })
					.then(({ data }) => [name, URL.createObjectURL(data)])
					// Ein einzelnes fehlendes Bild darf die anderen nicht mitreißen.
					// Fehlt es, bleibt der Eintrag leer und der Aufrufer zeigt seinen
					// Platzhalter — genau wie früher bei einem toten Bildlink.
					.catch(() => null)
			)
		).then(pairs => {
			const loaded = pairs.filter(Boolean)

			if (cancelled) {
				loaded.forEach(([, url]) => URL.revokeObjectURL(url))
				return
			}

			loaded.forEach(([name, url]) => {
				cache.current[name] = url
			})
			setUrls({ ...cache.current })
		})

		return () => {
			cancelled = true
		}
	}, [key, token])

	// Beim Verlassen der Komponente alles freigeben, was sie geladen hat.
	useEffect(() => {
		const loaded = cache.current
		return () => {
			Object.values(loaded).forEach(URL.revokeObjectURL)
		}
	}, [])

	return urls
}

/** Bequemer Sonderfall für genau ein Bild. */
export function useAvatarUrl(hashName) {
	const names = useMemo(() => (hashName ? [hashName] : []), [hashName])
	const urls = useAvatarUrls(names)
	return hashName ? urls[hashName] || '' : ''
}

export default useAvatarUrls
